import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/Accordion";
import { Checkbox } from "@/components/ui/Checkbox";
import SearchInput from "@/components/shared/SearchInput";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuth } from "@/hooks/useAuth";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import { PERMISSIONS } from "@/permissions/constant";
import { MatchRetriggerService } from "@/services/matchRetriggerService";
import { Sparkles, X } from "lucide-react";
import toast from "react-hot-toast";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

// Fallback only — the real ceiling is the server's MATCH_RETRIGGER_MAX_BATCH_SIZE,
// read off the response as `maxBatchSize`. Never hardcode the limit: the server
// rejects an over-limit batch outright (MATCH_RETRIGGER_BATCH_TOO_LARGE, never a
// silent truncation), so a stale client number turns a tunable setting into a
// 400 the user cannot explain.
const DEFAULT_MAX_BATCH_SIZE = 200;

// One page big enough to cover the display list. Group selection does NOT depend
// on this: it uses each group's whole-job eligibleApplicationIds.
const CANDIDATES_PER_PAGE = 200;

/**
 * JD-update re-trigger picker (Phase 3, §Phase 3
 * build). Opened after a successful job update whose critical JD fields
 * changed - never blocks the save itself (E19).
 *
 * Nothing is pre-selected by default (E7) — the user must act. Candidates in
 * a hired or rejected status are excluded by the API itself (plan §5.3a), so
 * this component never has to filter them out; statuses are dynamic and
 * tenant-configurable, so everything here is driven by the response, never by
 * a hardcoded status list.
 *
 * Ineligible candidates (no resume data, or a match already running) ARE
 * returned, flagged with `eligible: false` + `ineligibleReason`. They render
 * disabled rather than hidden, so the recruiter can see why someone can't be
 * re-scored instead of wondering where they went.
 *
 * `candidate.view` is checked here rather than at the call site because this
 * modal opens on its own, off a successful save. Its route asks for
 * `candidate.view` alongside `job.update` — the response carries candidate
 * names and their match scores — so a recruiter who may edit a job but not
 * read candidates got this modal and a 403 toast inside it. Nothing is
 * offered that the route will refuse.
 */
const RetriggerCandidatesModal = ({ open, onClose, onQueued, jobId, changedFields = [] }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusGroups, setStatusGroups] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [openStatuses, setOpenStatuses] = useState([]);
  const [search, setSearch] = useState("");
  const [maxBatchSize, setMaxBatchSize] = useState(DEFAULT_MAX_BATCH_SIZE);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const debouncedSearch = useDebounce(search, 400);
  const { user } = useAuth();
  const mayReadCandidates = hasPermission(PERMISSIONS.CANDIDATE_VIEW, getUserPermissions(user));

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearch("");
  }, [open, jobId]);

  const fetchCandidates = useCallback(async () => {
    if (!open || !jobId || !mayReadCandidates) return;
    setLoading(true);
    try {
      const res = await MatchRetriggerService.getJobRetriggerCandidates(jobId, {
        search: debouncedSearch,
        page: 1,
        perPage: CANDIDATES_PER_PAGE,
      });
      setStatusGroups(res?.statusGroups || []);
      setCandidates(res?.candidates || []);
      // Server-driven, not a client constant — see DEFAULT_MAX_BATCH_SIZE.
      setMaxBatchSize(res?.maxBatchSize || DEFAULT_MAX_BATCH_SIZE);
      setTotalCandidates(res?.pagination?.total ?? 0);
    } catch (err) {
      console.error("Failed to load retrigger candidates:", err);
      toast.error(apiErrorMessageFormat(err, "Failed to load candidates"));
    } finally {
      setLoading(false);
    }
  }, [open, jobId, debouncedSearch, mayReadCandidates]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const candidatesByStatus = useMemo(() => {
    const map = new Map();
    for (const c of candidates) {
      const key = c.statusId || "unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return map;
  }, [candidates]);

  useEffect(() => {
    if (!statusGroups.length) {
      setOpenStatuses([]);
      return;
    }

    setOpenStatuses(statusGroups.map((group) => group.statusId || "unassigned"));
  }, [statusGroups]);

  const toggleOne = (applicationId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(applicationId)) next.delete(applicationId);
      else next.add(applicationId);
      return next;
    });
  };

  // Only ever select what the server will actually accept. An ineligible row
  // (no resume data, or a match already running) is skipped at enqueue, so
  // letting it be selected would show "10 selected" then "7 queued".
  //
  // Selects the group's WHOLE-JOB eligible ids (group.eligibleApplicationIds),
  // not the rows currently loaded: candidates[] is a filtered, paginated view,
  // so selecting from it would silently pick only the visible subset on a job
  // with more applicants than one page. See plan E6.
  const toggleStatusGroup = (group) => {
    const ids = group?.eligibleApplicationIds || [];
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Whole-job across every status group, for the same reason as above.
  const selectableCandidateIds = useMemo(() => statusGroups.flatMap((group) => group.eligibleApplicationIds || []), [statusGroups]);
  const allCandidatesSelected = selectableCandidateIds.length > 0 && selectableCandidateIds.every((id) => selected.has(id));

  const toggleSelectAllCandidates = (checked) => {
    if (!checked) {
      setSelected(new Set());
      return;
    }

    setSelected(new Set(selectableCandidateIds));
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const res = await MatchRetriggerService.createBatch({
        triggerSource: "JOB_UPDATE",
        jobId,
        applicationIds: Array.from(selected),
        // Recorded on the batch + audit row so the ledger answers
        // "what changed" without anyone diffing the JD by hand.
        changedFields,
        reason: changedFields?.length ? "JD updated: " + changedFields.join(", ") : "JD updated",
      });
      toast.success(`Queued ${res?.queuedCount ?? selected.size} candidate${(res?.queuedCount ?? selected.size) === 1 ? "" : "s"} for re-analysis.`);
      if (res?.skipped?.length) {
        toast(`${res.skipped.length} candidate(s) were skipped (already in progress or ineligible).`);
      }
      // Hand the batch to the parent BEFORE closing: it is what starts the
      // progress panel and arms the item/done websocket handlers. Without
      // this the whole batch-progress feature is unreachable — no progress,
      // no completion toast, no refresh.
      if (res?.batchId) {
        onQueued?.({ batchId: res.batchId, queuedCount: res.queuedCount ?? selected.size });
      }
      onClose?.();
    } catch (err) {
      console.error("Failed to queue re-trigger batch:", err);
      toast.error(apiErrorMessageFormat(err, "Failed to queue re-analysis"));
    } finally {
      setSubmitting(false);
    }
  };

  const overLimit = selected.size > maxBatchSize;
  // candidates[] is one page; a group's counts and selectable ids are whole-job.
  const hasUnloadedCandidates = totalCandidates > candidates.length;

  if (!mayReadCandidates) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !submitting) onClose?.();
      }}
    >
      <DialogContent className="w-[min(1060px,calc(100vw-2rem))] max-h-[88vh] flex flex-col overflow-hidden p-0 rounded-[28px]">
        <DialogHeader className="relative shrink-0 overflow-hidden border-b border-border-primary/80  px-5 py-4">
          <div className="relative flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-border-primary bg-background-primary/60 px-2.5 py-0.5 text-[10px] font-medium text-text-secondary">
                <Sparkles size={12} />
                Re-analyse candidates after a JD update
              </div>
              <DialogTitle className="text-[16px] font-semibold tracking-[-0.02em] text-text-primary">Re-analyse candidates for this job?</DialogTitle>
              <DialogDescription className="max-w-2xl text-[12px] leading-[1.15] text-text-primary/70">
                You changed fields the AI match uses. Pick which candidates to re-analyse - nothing is selected by default.
              </DialogDescription>
            </div>
            <DialogClose
              className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border-primary bg-background-primary/80 text-text-secondary shadow-sm transition hover:bg-background-primary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
              disabled={submitting}
            >
              <X size={15} strokeWidth={2.75} />
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="shrink-0 border-b border-border-primary/80 bg-background-secondary px-5 py-3">
          <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search candidates..."
              className="w-full lg:max-w-[360px]"
              inputClassName="rounded-2xl bg-background-primary/70 border-border-primary/80 pl-11 pr-11 py-2 shadow-sm"
            />
            <label className="inline-flex items-center gap-2 rounded-2xl border border-border-primary bg-background-primary/70 px-3 py-1.5 text-[11px] text-text-primary shadow-sm lg:ml-auto">
              <Checkbox checked={allCandidatesSelected} onCheckedChange={(checked) => toggleSelectAllCandidates(Boolean(checked))} />
              Select all candidates
            </label>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-background-primary/30 px-3 py-3">
          {loading ? (
            <div className="py-10 text-center text-[12px] text-text-secondary">Loading candidates...</div>
          ) : statusGroups.length === 0 ? (
            <div className="py-10 text-center text-[12px] text-text-secondary">No candidates on this job.</div>
          ) : (
            <Accordion openMultiple value={openStatuses} onValueChange={setOpenStatuses} className="space-y-2">
              {statusGroups.map((group) => {
                const groupCandidates = candidatesByStatus.get(group.statusId || "unassigned") || [];

                // Rendered even with no loaded rows: the group is
                // real (its counts are whole-job) and its checkbox
                // selects the whole group, so hiding it on an empty
                // page would hide selectable candidates entirely.
                const groupEligibleIds = group.eligibleApplicationIds || [];
                const allSelected = groupEligibleIds.length > 0 && groupEligibleIds.every((id) => selected.has(id));
                const notLoadedCount = group.totalCount - groupCandidates.length;

                return (
                  <AccordionItem
                    key={group.statusId || "unassigned"}
                    value={group.statusId || "unassigned"}
                    className="overflow-hidden rounded-3xl border border-border-primary/80 bg-background-secondary/80 shadow-sm transition"
                  >
                    <AccordionTrigger className="px-3.5 py-2.5 bg-background-tertiary/35">
                      <div className="flex flex-1 items-center gap-2.5 pr-3" onClick={(e) => e.stopPropagation()}>
                        <label className="flex items-center gap-2.5" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={allSelected} disabled={groupEligibleIds.length === 0} onCheckedChange={() => toggleStatusGroup(group)} />
                          <span className="text-[13px] font-semibold tracking-[-0.01em] text-text-primary">{group.statusName}</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-text-secondary">
                        <span>
                          {group.eligibleCount} of {group.totalCount} selectable
                        </span>
                        {group.inProgressCount > 0 && <span>{group.inProgressCount} in progress</span>}
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="px-2 pb-2 pt-0 sm:px-2.5">
                      {groupCandidates.map((c) => {
                        const isSelected = selected.has(c.applicationId);
                        return (
                          <div className="mb-1" key={c.applicationId}>
                            <label
                              className={`group flex items-center gap-2.5 rounded-2xl border px-3 py-1.5 transition ${
                                isSelected
                                  ? "border-button-secondary/50 bg-button-secondary/8 shadow-[0_0_0_1px_rgba(0,0,0,0.02)]"
                                  : "border-transparent hover:border-border-primary hover:bg-background-primary/70"
                              } ${c.eligible ? "" : "opacity-60"}`}
                            >
                              <Checkbox checked={isSelected} disabled={!c.eligible} onCheckedChange={() => toggleOne(c.applicationId)} />
                              <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-text-primary">{c.fullName}</span>
                              {!c.eligible && <span className="shrink-0 text-[10px] text-text-muted">{(c.ineligibleReason || "").replace(/_/g, " ").toLowerCase()}</span>}
                              {c.matchScore != null && (
                                <span className="rounded-full border border-border-primary bg-background-primary px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                                  {(c.matchScore / 10).toFixed(1)}/10
                                </span>
                              )}
                            </label>
                          </div>
                        );
                      })}

                      {notLoadedCount > 0 && (
                        <div className="px-3 py-1.5 text-[10px] text-text-secondary">
                          {groupCandidates.length === 0 ? `${notLoadedCount} candidate(s) in this status are not shown` : `+${notLoadedCount} more not shown`}
                          {" — the group checkbox above still selects all "}
                          {group.eligibleCount} selectable.
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>

        <div className="shrink-0 border-t border-border-primary/80 bg-background-secondary px-5 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className={`text-[11px] ${overLimit ? "text-red-400 font-medium" : "text-text-secondary"}`}>
              {selected.size} selected
              {overLimit && <span className="ml-2">- max {maxBatchSize} per batch</span>}
              {!overLimit && hasUnloadedCandidates && (
                <span className="ml-2 text-text-muted">
                  (showing {candidates.length} of {totalCandidates})
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="h-9 rounded-2xl border-border-primary bg-background-primary/70 px-4 text-[11px]"
                type="button"
                disabled={submitting}
                onClick={() => onClose?.()}
              >
                Cancel
              </Button>
              <Button
                className="h-9 rounded-2xl bg-button-secondary px-4 text-text-base shadow-sm transition hover:-translate-y-px"
                type="button"
                disabled={submitting || selected.size === 0 || overLimit}
                onClick={handleSubmit}
              >
                {submitting ? "Queuing..." : `Re-analyse ${selected.size || ""}`}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RetriggerCandidatesModal;
